/**
 * Vendor PQ Application — fully redesigned for the Ellora EPC brand.
 * Standalone route (/partner/application) — has its own header.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import {
  CheckCircle2, Upload, Loader2, ChevronRight, ChevronLeft,
  Send, AlertCircle, Building2, LogOut,
} from 'lucide-react';

const STATUS_COLOR = {
  draft:                'bg-slate-100 text-slate-500 border-slate-200',
  submitted:            'bg-blue-50 text-blue-700 border-blue-200',
  under_review:         'bg-amber-50 text-amber-700 border-amber-200',
  clarification_needed: 'bg-orange-50 text-orange-700 border-orange-200',
  approved:             'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:             'bg-red-50 text-red-600 border-red-200',
};

/* ── minimal form field styles ── */
const inp = 'w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872]/20 transition-colors';
const lbl = 'block text-xs font-semibold text-slate-600 mb-1';

export default function VendorApplication() {
  const { vendorUser, vendorFetch, logout } = useVendorAuth();
  const [template, setTemplate] = useState(null);
  const [app, setApp] = useState(null);
  const [pq, setPq] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    vendorFetch('/partner/pq-template').then(setTemplate).catch(() => {});
    vendorFetch('/partner/application').then(d => { setApp(d); setPq(d.pq_responses || {}); }).catch(() => {});
  }, []);

  if (!template || !app) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-[#3b4872]" />
    </div>
  );

  const sections = template.sections || [];
  const section = sections[sectionIdx];
  const canEdit = ['draft', 'clarification_needed'].includes(app.status);
  const secPq = pq[section?.key] || {};

  const setField = (secKey, fieldKey, value) =>
    setPq(p => ({ ...p, [secKey]: { ...(p[secKey] || {}), [fieldKey]: value } }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      await vendorFetch('/partner/application', { method: 'PUT', body: JSON.stringify({ pq_responses: pq }) });
      toast.success('Draft saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const uploadFile = async (field, file) => {
    setUploading(u => ({ ...u, [field.key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('vendor_token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner/application/documents?doc_type=${field.key.replace('_upload', '')}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }
      );
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Upload failed'); }
      const d = await r.json();
      setField(section.key, field.key, d.doc?.file_url || 'uploaded');
      toast.success(`${field.label} uploaded`);
    } catch (e) { toast.error(e?.message || 'Upload failed'); }
    setUploading(u => ({ ...u, [field.key]: false }));
  };

  const submitApp = async () => {
    setSubmitting(true);
    try {
      await vendorFetch('/partner/application', { method: 'PUT', body: JSON.stringify({ pq_responses: pq }) });
      await vendorFetch('/partner/application/submit', { method: 'POST' });
      toast.success('Application submitted to Ellora EPC procurement!');
      const d = await vendorFetch('/partner/application');
      setApp(d);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submission failed'); }
    setSubmitting(false);
  };

  /* progress */
  const filledSections = sections.filter(s => pq[s.key] && Object.keys(pq[s.key] || {}).length > 0).length;
  const progress = Math.round((filledSections / sections.length) * 100);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* ── Header ── */}
      <header className="sticky top-0 z-30 bg-[#1c2a4d] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/partner/dashboard" className="flex items-center gap-2.5">
            <img src="/ellora-logo-blue.png" alt="Ellora EPC" className="h-7 w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
            <span className="text-white/50 text-[11px] border-l border-white/20 pl-2.5 font-medium">Partner Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-white/60 text-xs truncate max-w-[160px]">{vendorUser?.company_name}</span>
            <button onClick={logout} className="text-white/50 hover:text-white transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 space-y-6">
        {/* ── Page title ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Pre-qualification Application</h1>
            <p className="text-sm text-slate-500 mt-1">Complete all sections and upload required documents to be empanelled as a vendor.</p>
          </div>
          <span className={`text-[11px] px-3 py-1.5 border rounded-sm font-bold uppercase tracking-wider shrink-0 ${STATUS_COLOR[app.status] || STATUS_COLOR.draft}`}>
            {app.status?.replace(/_/g, ' ')}
          </span>
        </div>

        {/* ── Progress bar ── */}
        <div>
          <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
            <span>{filledSections}/{sections.length} sections started</span>
            <span>{progress}% complete</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#3b4872] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* ── Clarification requests ── */}
        {(app.clarification_requests || []).filter(cr => !cr.vendor_response).map((cr, i) => (
          <div key={i} className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-sm p-4">
            <AlertCircle size={16} className="text-orange-500 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="text-sm text-orange-900 font-semibold">Clarification Requested</p>
              <p className="text-sm text-orange-800">{cr.question}</p>
              <textarea rows={2} placeholder="Your response…"
                className="w-full border border-orange-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-white"
                onChange={e => {
                  const updated = [...(app.clarification_requests || [])];
                  updated[i] = { ...updated[i], _draft_response: e.target.value };
                  setApp({ ...app, clarification_requests: updated });
                }} />
              <button onClick={async () => {
                const resp = app.clarification_requests[i]._draft_response;
                if (!resp?.trim()) { toast.error('Enter a response'); return; }
                try {
                  await vendorFetch('/partner/application/clarify', { method: 'POST', body: JSON.stringify({ clarification_index: i, response: resp }) });
                  toast.success('Response submitted');
                  const d = await vendorFetch('/partner/application'); setApp(d);
                } catch (e) { toast.error('Failed to submit'); }
              }} className="px-4 py-2 bg-orange-600 text-white rounded-sm text-xs font-bold">
                Submit Response
              </button>
            </div>
          </div>
        ))}

        {/* ── Section tabs ── */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {/* Tab bar */}
          <div className="flex overflow-x-auto border-b border-slate-200 bg-slate-50">
            {sections.map((s, i) => {
              const filled = pq[s.key] && Object.keys(pq[s.key] || {}).length > 0;
              const active = i === sectionIdx;
              return (
                <button key={s.key} onClick={() => setSectionIdx(i)}
                  className={`flex items-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors shrink-0 ${
                    active
                      ? 'border-[#3b4872] text-[#3b4872] bg-white'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-white'
                  }`}
                  data-testid={`pq-tab-${s.key}`}>
                  {filled && !active && <CheckCircle2 size={12} className="text-emerald-500" />}
                  {i + 1}. {s.title}
                </button>
              );
            })}
          </div>

          {/* Section body */}
          <div className="p-6 space-y-5">
            <h2 className="font-display font-bold text-lg text-slate-900">{section.title}</h2>

            {/* Fields grid */}
            <div className="grid sm:grid-cols-2 gap-5">
              {section.fields.map((field) => {
                const val = secPq[field.key];
                return (
                  <div key={field.key} className={field.type === 'text' && field.key.includes('address') ? 'sm:col-span-2' : ''}>
                    <label className={lbl}>
                      {field.label}
                      {field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {field.help_text && (
                      <p className="text-[10px] text-slate-400 mb-1">{field.help_text}</p>
                    )}

                    {field.type === 'file' ? (
                      <div>
                        {val ? (
                          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2.5">
                            <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
                            <span className="text-sm text-emerald-700 font-medium flex-1">Uploaded successfully</span>
                            {canEdit && (
                              <button onClick={() => setField(section.key, field.key, null)} className="text-slate-400 hover:text-red-500 text-[10px] underline">Remove</button>
                            )}
                          </div>
                        ) : (
                          <label className={`flex items-center gap-3 border-2 border-dashed rounded-sm px-4 py-3 cursor-pointer transition-colors ${
                            canEdit ? 'border-slate-300 hover:border-[#3b4872] hover:bg-[#3b4872]/5' : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                          }`}>
                            {uploading[field.key] ? (
                              <Loader2 size={16} className="animate-spin text-[#3b4872]" />
                            ) : (
                              <Upload size={16} className="text-slate-400" />
                            )}
                            <span className="text-sm text-slate-500">
                              {uploading[field.key] ? 'Uploading…' : 'Click to upload PDF or JPG'}
                            </span>
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={!canEdit}
                              onChange={e => e.target.files?.[0] && uploadFile(field, e.target.files[0])} />
                          </label>
                        )}
                      </div>

                    ) : field.type === 'select' ? (
                      <select value={secPq[field.key] || ''} onChange={e => setField(section.key, field.key, e.target.value)}
                        disabled={!canEdit} className={inp + (canEdit ? '' : ' bg-slate-50 text-slate-400')}
                        data-testid={`pq-${section.key}-${field.key}`}>
                        <option value="">— Select —</option>
                        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>

                    ) : field.type === 'multiselect' ? (
                      <div className="flex flex-wrap gap-2 sm:col-span-2">
                        {(field.options || []).map(o => {
                          const selected = (secPq[field.key] || []).includes(o);
                          return (
                            <button key={o} type="button" disabled={!canEdit}
                              onClick={() => {
                                const cur = secPq[field.key] || [];
                                setField(section.key, field.key, selected ? cur.filter(x => x !== o) : [...cur, o]);
                              }}
                              className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${
                                selected
                                  ? 'bg-[#3b4872] text-white border-[#3b4872]'
                                  : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872]'
                              } disabled:opacity-50`}>
                              {o}
                            </button>
                          );
                        })}
                      </div>

                    ) : (
                      <input type={field.type === 'number' ? 'number' : 'text'}
                        value={secPq[field.key] || ''}
                        onChange={e => setField(section.key, field.key, e.target.value)}
                        disabled={!canEdit}
                        className={inp + (canEdit ? '' : ' bg-slate-50 text-slate-400')}
                        data-testid={`pq-${section.key}-${field.key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Footer nav ── */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50 flex-wrap gap-3">
            <div className="flex gap-2">
              {sectionIdx > 0 && (
                <button onClick={() => setSectionIdx(s => s - 1)}
                  className="flex items-center gap-1.5 px-4 py-2 border border-slate-300 bg-white text-slate-600 rounded-sm text-sm font-medium hover:bg-slate-50 transition-colors">
                  <ChevronLeft size={15} /> Previous
                </button>
              )}
              {sectionIdx < sections.length - 1 && (
                <button onClick={() => setSectionIdx(s => s + 1)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm text-sm font-medium transition-colors">
                  Next <ChevronRight size={15} />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              {canEdit && (
                <button onClick={saveDraft} disabled={saving}
                  className="px-5 py-2 border border-[#3b4872] text-[#3b4872] bg-white hover:bg-[#3b4872]/5 rounded-sm text-sm font-semibold disabled:opacity-50 transition-colors"
                  data-testid="save-draft-btn">
                  {saving ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}
                  {saving ? 'Saving…' : 'Save Draft'}
                </button>
              )}
              {canEdit && sectionIdx === sections.length - 1 && (
                <button onClick={submitApp} disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2 bg-[#1c2a4d] hover:bg-[#3b4872] text-white rounded-sm text-sm font-semibold disabled:opacity-50 transition-colors"
                  data-testid="submit-application-btn">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Help strip ── */}
        <p className="text-center text-xs text-slate-400">
          Need help?{' '}
          <a href="mailto:procurement@elloraepc.com" className="text-[#3b4872] hover:underline">Contact Ellora EPC Procurement</a>
        </p>
      </div>
    </div>
  );
}
